import translate from '@translate';
import React, { FC, useMemo, useState } from 'react';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { styled } from '@mui/system';
import Checkbox from 'src/components/Checkbox';
import ThreeStateCheckbox from 'src/components/ThreeStateCheckbox';
import MuiGrid from '@mui/material/Grid';
import { SelectValueType } from 'src/components/Select';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { colors, sizes } from 'src/themes/default';
import getCountryOptions from 'src/utils/countryOptions';
import {
    CountryServiceGroupType,
    getCountryCheckboxState,
    getCountryServiceCounts,
    getShipperServiceCounts,
    groupServiceCodesByShipper,
    isServiceSelected,
    toggleCountrySelection,
    toggleServiceSelection,
} from './functions';

const TreeContainer = styled('div')({
    padding: '8px 12px',

    '& .MuiTreeItem-content': {
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: 'transparent',
        },
        '&.Mui-focused': {
            backgroundColor: 'transparent',
        },
        '&.Mui-selected': {
            backgroundColor: 'transparent',
        },
    },

    '& .MuiTreeItem-group': {
        backgroundColor: 'transparent',
    },

    '& .MuiTreeItem-label': {
        fontSize: sizes.body_FS,
        lineHeight: sizes.body_LH,
        color: colors.text_T1,
    },

    '& svg': {
        color: colors.icon,
        fill: colors.icon,
    },
});

const ContentBox = styled(Box)({
    borderRadius: sizes.radius,
    border: `${sizes.border}px solid ${colors.text_T3}`,
    backgroundColor: colors.background_W,
    marginTop: '4px',
});

const FolderLabel = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: '26px',
});

const FolderLabelWithCheckbox = styled(FolderLabel)({
    position: 'relative',
    paddingRight: '28px',

    '& .three-state-checkbox': {
        position: 'absolute',
        right: 0,
        top: 0,
    },
});

const CheckboxWrapper = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: sizes.body_LH,
    width: '24px',
    flexShrink: 0,
});

const ServiceRow = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ selected }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '4px',
    padding: '2px 4px',
    borderRadius: '4px',
    backgroundColor: selected ? colors.primary_1 : 'transparent',
    cursor: 'pointer',
}));

const ServiceLabel = styled('span')({
    flex: 1,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    fontSize: sizes.body_FS,
    lineHeight: sizes.body_LH,
    color: colors.text_T1,
    paddingTop: 0,
});

export interface CarrierServiceCodesPickerPropsType {
    serviceCodes: ConstantsCodeType[];
    shipperCodes: ConstantsCodeType[];
    selectedServiceCodes: SelectValueType[];
    setSelectedServiceCodes: React.Dispatch<React.SetStateAction<SelectValueType[]>>;
    disabled: boolean;
}

interface ServiceCheckboxGridPropsType {
    services: ConstantsCodeType[];
    selectedServiceCodes: SelectValueType[];
    onToggleService: (service: ConstantsCodeType) => void;
    disabled: boolean;
}

const ServiceCheckboxGrid: FC<ServiceCheckboxGridPropsType> = (props) => (
    <Box sx={{ pl: 2, py: 1 }}>
        <MuiGrid container spacing={1}>
            {props.services.map((service) => {
                const selected = isServiceSelected(service, props.selectedServiceCodes);
                return (
                    <MuiGrid item xs={12} md={4} key={`${service.shipperCode}-${service.value}`}>
                        <ServiceRow
                            selected={selected}
                            sx={props.disabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
                            onClick={() => {
                                if (!props.disabled) {
                                    props.onToggleService(service);
                                }
                            }}
                        >
                            <CheckboxWrapper>
                                <Checkbox
                                    value={selected}
                                    onChange={() => {
                                        if (!props.disabled) {
                                            props.onToggleService(service);
                                        }
                                    }}
                                    sx={{
                                        padding: 0,
                                        width: '18px',
                                        height: '18px',
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '18px',
                                        },
                                        ...(props.disabled ? { opacity: 0.5, pointerEvents: 'none' } : {}),
                                    }}
                                />
                            </CheckboxWrapper>
                            <ServiceLabel>{service.label}</ServiceLabel>
                        </ServiceRow>
                    </MuiGrid>
                );
            })}
        </MuiGrid>
    </Box>
);

interface CountryFolderPropsType {
    shipperCode: string;
    countryGroup: CountryServiceGroupType;
    selectedServiceCodes: SelectValueType[];
    onToggleCountry: (services: ConstantsCodeType[]) => void;
    onToggleService: (service: ConstantsCodeType) => void;
    disabled: boolean;
}

const CountryFolder: FC<CountryFolderPropsType> = (props) => {
    const counts = getCountryServiceCounts(props.countryGroup.services, props.selectedServiceCodes);
    const checkboxState = getCountryCheckboxState(props.countryGroup.services, props.selectedServiceCodes);

    return (
        <TreeItem
            nodeId={`${props.shipperCode}-${props.countryGroup.countryCode}`}
            label={
                <FolderLabelWithCheckbox>
                    <span>
                        {props.countryGroup.label} ({counts.active} / {counts.total})
                    </span>
                    <ThreeStateCheckbox
                        value={checkboxState}
                        disabled={props.disabled}
                        onChange={() => props.onToggleCountry(props.countryGroup.services)}
                    />
                </FolderLabelWithCheckbox>
            }
        >
            <ServiceCheckboxGrid
                services={props.countryGroup.services}
                selectedServiceCodes={props.selectedServiceCodes}
                onToggleService={props.onToggleService}
                disabled={props.disabled}
            />
        </TreeItem>
    );
};

const CarrierServiceCodesPicker: FC<CarrierServiceCodesPickerPropsType> = (props) => {
    const [expanded, setExpanded] = useState<string[]>([]);
    const countryOptions = useMemo(() => getCountryOptions(), []);
    const otherLabel = translate('providerDetail.serviceCodesOther');

    const shipperGroups = useMemo(
        () =>
            groupServiceCodesByShipper(
                props.serviceCodes,
                props.shipperCodes,
                countryOptions,
                otherLabel
            ),
        [props.serviceCodes, props.shipperCodes, countryOptions, otherLabel]
    );

    const handleToggleService = (service: ConstantsCodeType) => {
        props.setSelectedServiceCodes((current) => toggleServiceSelection(service, current));
    };

    const handleToggleServiceGroup = (services: ConstantsCodeType[]) => {
        props.setSelectedServiceCodes((current) => toggleCountrySelection(services, current));
    };

    const handleToggle = (_event: React.SyntheticEvent, nodeIds: string[]) => {
        setExpanded(nodeIds);
    };

    return (
        <FormControl fullWidth>
            <InputLabel
                shrink
                sx={{
                    fontSize: sizes.body_FS,
                    lineHeight: sizes.body_LH,
                    fontWeight: sizes.bold_W,
                    color: colors.text_T1,
                    position: 'relative',
                    transform: 'none',
                    display: 'block',
                    marginBottom: '4px',
                }}
            >
                {translate('providerDetail.serviceCodes')}
            </InputLabel>
            <ContentBox>
                <TreeContainer>
                    <TreeView
                        defaultCollapseIcon={<FolderOpenIcon />}
                        defaultExpandIcon={<FolderCopyIcon />}
                        disableSelection
                        expanded={expanded}
                        onNodeToggle={handleToggle}
                    >
                        {shipperGroups.map((shipperGroup) => {
                            const counts = getShipperServiceCounts(
                                shipperGroup.services,
                                props.selectedServiceCodes
                            );
                            const shipperCheckboxState = getCountryCheckboxState(
                                shipperGroup.services,
                                props.selectedServiceCodes
                            );

                            return (
                                <TreeItem
                                    key={shipperGroup.shipperCode}
                                    nodeId={shipperGroup.shipperCode}
                                    label={
                                        <FolderLabelWithCheckbox>
                                            <span>
                                                {shipperGroup.shipperLabel} ({counts.active} / {counts.total})
                                            </span>
                                            <ThreeStateCheckbox
                                                value={shipperCheckboxState}
                                                disabled={props.disabled}
                                                onChange={() => handleToggleServiceGroup(shipperGroup.services)}
                                            />
                                        </FolderLabelWithCheckbox>
                                    }
                                >
                                    {shipperGroup.countryGroups
                                        ? shipperGroup.countryGroups.map((countryGroup) => (
                                              <CountryFolder
                                                  key={`${shipperGroup.shipperCode}-${countryGroup.countryCode}`}
                                                  shipperCode={shipperGroup.shipperCode}
                                                  countryGroup={countryGroup}
                                                  selectedServiceCodes={props.selectedServiceCodes}
                                                  onToggleCountry={handleToggleServiceGroup}
                                                  onToggleService={handleToggleService}
                                                  disabled={props.disabled}
                                              />
                                          ))
                                        : (
                                            <ServiceCheckboxGrid
                                                services={shipperGroup.services}
                                                selectedServiceCodes={props.selectedServiceCodes}
                                                onToggleService={handleToggleService}
                                                disabled={props.disabled}
                                            />
                                        )}
                                </TreeItem>
                            );
                        })}
                    </TreeView>
                </TreeContainer>
            </ContentBox>
        </FormControl>
    );
};

export default CarrierServiceCodesPicker;
